/**
 * The button grid's look rows in the Inspector (DEC-316 / DEC-317, built 2026-10-27): Starts in What it does, and in
 * How it looks the Finish, Title, Icon and where it sits, the Texture (pattern, size, colour, strength) and the Beat
 * flash. Values and words are the study's (`public/button-look-study.html`), the controls the Inspector's own field
 * and pill classes. DEC-318's reshaping of the whole panel (dividers, ? tips, the preview) comes separately.
 */

import { For, Show } from 'solid-js';
import { Tip } from './Tip.tsx';
import type { ButtonGridItem, FaceOptions } from '../layout/projectStore.ts';
/** Anything with the look fields: a button grid, or a single pad (DEC-321). */
type LookItem = FaceOptions & { id: string };
import { HUE_ORDER } from '../layout/projectStore.ts';
import { editItem } from '../layout/store.ts';
import { INSTRUMENT_ICONS, ICON_NAMES, type InstrumentIconId } from './InstrumentIcon.tsx';
import {
  LOOK_DEFAULTS, TEX_NAMES, textureFor,
  type GridFinish, type GridTitleAt, type GridIconAt, type GridTexture, type GridTexSize, type GridStarts, type GridBeatFlash,
} from '../layout/buttonLook.ts';

const STARTS: [GridStarts, string][] = [['now', 'Now'], ['beat', 'Beat'], ['bar', 'Bar'], ['2bar', '2 bars'], ['4bar', '4 bars']];
const FINISHES: [GridFinish, string][] = [['lit', 'Lit glass'], ['glass', 'Glass'], ['pads', 'Pads']];
const TITLES: [GridTitleAt, string][] = [
  ['tl', 'Top left'], ['tc', 'Top centre'], ['tr', 'Top right'],
  ['bl', 'Bottom left'], ['bc', 'Bottom centre'], ['br', 'Bottom right'], ['side', 'Up the side'], ['off', 'Off'],
];
const ICON_AT: [GridIconAt, string][] = [['title', 'Title'], ['corner', 'Corner'], ['above', 'Above'], ['off', 'Off']];
const TEXTURES: GridTexture[] = ['auto', 'smooth', 'dimples', 'ribs-v', 'ribs-h', 'waves', 'knurl', 'rings', 'grid', 'diag'];
const TEX_SIZES: [GridTexSize, string][] = [['auto', 'Auto'], ['fine', 'Fine'], ['medium', 'Med'], ['coarse', 'Coarse']];
const FLASHES: [GridBeatFlash, string][] = [['off', 'Off'], ['pulse', 'Pulse'], ['breathe', 'Breathe'], ['blink', 'Blink']];

function Pills<T extends string>(props: { label: string; options: [T, string][]; value: T; onPick: (v: T) => void;
  disabled?: boolean; title?: string | undefined; field: string; bare?: boolean; tip?: string }) {
  const pills = () => (
    <div class="inspector-pills" data-look-field={props.field} title={props.title}>
        <span class="inspector-pills-label">{props.label}<Show when={props.tip}><Tip text={props.tip ?? ''} /></Show></span>
        <For each={props.options}>
          {([v, l]) => (
            <button
              type="button"
              class="inspector-pill"
              data-value={v}
              data-selected={props.value === v ? 'true' : 'false'}
              disabled={props.disabled}
              onClick={() => props.onPick(v)}
            >
              {l}
            </button>
          )}
        </For>
    </div>
  );
  // `bare` inside a zone that already pads its rows (What it does' grid zone).
  return props.bare ? pills() : <div class="inspector-zone">{pills()}</div>;
}

/** What it does: Starts. Visual only (OPButtons sends at once); the tip says to match Live's launch quantisation. */
export function GridStartsRow(props: { item: ButtonGridItem }) {
  return (
    <Pills
      bare
      field="starts"
      label="Starts"
      options={STARTS}
      value={props.item.starts ?? 'now'}
      onPick={(v) => editItem(props.item.id, { starts: v })}
      tip="When a pressed pad lights. OPButtons sends the press at once; Live waits for its launch quantisation, so set this to match Live (1 Bar = Bar). Now lights at once."
    />
  );
}

/** The shared reads every look row needs. */
function look(item: () => LookItem) {
  const icon = () => (item().icon && item().icon !== 'none' ? item().icon as InstrumentIconId : undefined);
  return {
    set: (patch: Partial<FaceOptions>) => editItem(item().id, patch),
    icon,
    auto: () => textureFor(icon() ?? 'none', 'auto', 'auto'),
    tex: () => textureFor(icon() ?? 'none', item().texture, item().texSize),
    startsNow: () => ((item() as Partial<ButtonGridItem>).starts ?? 'now') === 'now',
  };
}

/** How it looks, with the colour: the finish. */
export function GridFinishRow(props: { item: LookItem }) {
  const l = look(() => props.item);
  return <Pills field="finish" label="Finish" options={FINISHES} value={props.item.finish ?? LOOK_DEFAULTS.finish} onPick={(v) => l.set({ finish: v })} />;
}

/** How it looks, with the name and font: where the title shows, the icon and where it sits. */
export function GridTitleIconRows(props: { item: ButtonGridItem }) {
  const it = () => props.item;
  const l = look(it);
  return (
    <>
      <div class="inspector-zone">
        <label class="inspector-field" data-look-field="titleAt">
          <span>Title</span>
          <select
            value={it().titleAt ?? LOOK_DEFAULTS.titleAt}
            onChange={(e) => l.set({ titleAt: e.currentTarget.value as GridTitleAt })}
            title={(it().name ?? '').trim() ? 'Where the grid’s name shows, inside the grid' : 'Give the grid a name to show a title'}
          >
            <For each={TITLES}>{([v, lab]) => <option value={v}>{lab}</option>}</For>
          </select>
        </label>
        <label class="inspector-field" data-look-field="icon">
          <span>Icon</span>
          <select value={it().icon ?? 'none'} onChange={(e) => l.set({ icon: e.currentTarget.value as InstrumentIconId | 'none' })}>
            <option value="none">None</option>
            <For each={[...INSTRUMENT_ICONS]}>{(id) => <option value={id}>{ICON_NAMES[id]}</option>}</For>
          </select>
        </label>
      </div>
      <Pills
        field="iconAt" label="Icon on" options={ICON_AT} value={it().iconAt ?? LOOK_DEFAULTS.iconAt}
        onPick={(v) => l.set({ iconAt: v })} disabled={!l.icon()} title={l.icon() ? undefined : 'Pick an icon first'}
      />
    </>
  );
}

/** How it looks, its own group: the texture's pattern, size, colour and strength. */
export function GridTextureRows(props: { item: LookItem }) {
  const it = () => props.item;
  const l = look(it);
  return (
    <>
      <div class="inspector-zone">
        <label class="inspector-field" data-look-field="texture">
          <span>Texture</span>
          <select value={it().texture ?? 'auto'} onChange={(e) => l.set({ texture: e.currentTarget.value as GridTexture })}>
            <For each={TEXTURES}>
              {(t) => (
                <option value={t}>
                  {t === 'auto' ? `Auto (${TEX_NAMES[l.auto().pattern]}${l.icon() ? '' : ', no icon'})` : TEX_NAMES[t]}
                </option>
              )}
            </For>
          </select>
        </label>
      </div>
      <Show when={l.tex().pattern !== 'smooth'}>
        <Pills field="texSize" label="Texture size" options={TEX_SIZES} value={it().texSize ?? 'auto'} onPick={(v) => l.set({ texSize: v })} />
        <div class="inspector-zone">
          <label class="inspector-field" data-look-field="texColour">
            <span>Texture colour</span>
            <select value={it().texColour ?? 'auto'} onChange={(e) => l.set({ texColour: e.currentTarget.value as NonNullable<FaceOptions['texColour']> })}>
              <option value="auto">Auto (the grid's)</option>
              <option value="dark">Dark (grooves only)</option>
              <For each={HUE_ORDER.filter((h) => h !== 'none')}>{(h) => <option value={h}>{h[0]!.toUpperCase() + h.slice(1)}</option>}</For>
            </select>
          </label>
          <label class="inspector-field" data-look-field="texStrength">
            <span>Strength</span>
            <input
              type="range" min="0" max="100" step="5"
              value={it().texStrength ?? LOOK_DEFAULTS.texStrength}
              onInput={(e) => l.set({ texStrength: Math.max(0, Math.min(100, Number(e.currentTarget.value))) })}
            />
            <b class="inspector-range-val">{it().texStrength ?? LOOK_DEFAULTS.texStrength}%</b>
          </label>
        </div>
      </Show>
    </>
  );
}

/** How it looks, its own group: the beat flash, dimmed with its reason when Starts is Now. */
export function GridFlashRow(props: { item: ButtonGridItem }) {
  const l = look(() => props.item);
  return (
    <Pills
      field="beatFlash" label="Beat flash" options={FLASHES}
      value={l.startsNow() ? 'off' : props.item.beatFlash ?? LOOK_DEFAULTS.beatFlash}
      onPick={(v) => editItem(props.item.id, { beatFlash: v })}
      disabled={l.startsNow()}
      tip={l.startsNow() ? 'Off while Starts is Now: a pad that starts at once never flashes.' : 'The lit pad pulses on the beat while the clock runs, never more than 3 times a second.'}
    />
  );
}

/** How it looks, with the name and font, for a row drawn as a tray (DEC-321): where its Name shows in line. */
export function TrayTitleRow(props: { item: { id: string; name?: string | undefined; titleAt?: GridTitleAt | undefined } }) {
  return (
    <div class="inspector-zone">
      <label class="inspector-field" data-look-field="titleAt">
        <span>Title<Tip text="The item's name, in line at the edge of its tray. A label with the same words hides while it shows." /></span>
        <select value={props.item.titleAt ?? LOOK_DEFAULTS.titleAt} onChange={(e) => editItem(props.item.id, { titleAt: e.currentTarget.value as GridTitleAt })}>
          <For each={TITLES}>{([v, lab]) => <option value={v}>{lab}</option>}</For>
        </select>
      </label>
    </div>
  );
}

const PAD_ICON_AT: [GridIconAt, string][] = [['above', 'Above'], ['corner', 'Corner'], ['off', 'Off']];

/** How it looks, with the name and font, for a single pad (DEC-321): its icon and where it sits. No title band on a
 *  single pad, so Above (the default once an icon is picked), Corner or Off. */
export function PadIconRows(props: { item: LookItem }) {
  const it = () => props.item;
  const l = look(it);
  const at = () => { const a = it().iconAt ?? 'above'; return a === 'title' ? 'above' : a; };
  return (
    <>
      <div class="inspector-zone">
        <label class="inspector-field" data-look-field="icon">
          <span>Icon</span>
          <select value={it().icon ?? 'none'} onChange={(e) => l.set({ icon: e.currentTarget.value as InstrumentIconId | 'none' })}>
            <option value="none">None</option>
            <For each={[...INSTRUMENT_ICONS]}>{(id) => <option value={id}>{ICON_NAMES[id]}</option>}</For>
          </select>
        </label>
      </div>
      <Pills field="iconAt" label="Icon on" options={PAD_ICON_AT} value={at()}
        onPick={(v) => l.set({ iconAt: v })} disabled={!l.icon()} title={l.icon() ? undefined : 'Pick an icon first'} />
    </>
  );
}
